import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';

export default function Layout() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // ハッシュアンカーへのスクロール処理
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const scrollToElement = () => {
        const el = document.getElementById(id);
        if (el) {
          const headerOffset = 80;
          const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      };
      // ページレンダリング後にスクロール
      const timer = setTimeout(scrollToElement, 100);
      return () => clearTimeout(timer);
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' as 'auto' | 'instant' | 'smooth' });
    }
  }, [location]);

  const navItems = [
    { name: 'TOP', path: '/' },
    { name: 'SERVICE', path: '/service' },
    { name: 'WORKS', path: '/works' },
    { name: 'COMPANY', path: '/company' },
    { name: 'CONTACT', path: '/contact' }
  ];

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-sm shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-6 py-5">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img
                src="https://static.readdy.ai/image/a547e5108220e49fc6552e81382cf2b5/6231042b200aa81403b8b2d064222f5d.png"
                alt="Lill Logo"
                className="h-8 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-10">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative text-sm font-medium tracking-wide transition-colors duration-200 whitespace-nowrap ${
                    isActive(item.path)
                      ? 'text-[#1a2744]'
                      : 'text-gray-600 hover:text-[#1a2744]'
                  }`}
                >
                  {item.name}
                  {isActive(item.path) && (
                    <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-[#2563EB]" />
                  )}
                </Link>
              ))}

              <Link
                to="/contact"
                className="px-8 py-2.5 bg-[#2563EB] text-white text-sm font-medium rounded transition-all duration-200 hover:bg-[#1d4ed8] whitespace-nowrap"
              >
                お問い合わせ
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-[#1a2744] hover:bg-gray-100 rounded transition-colors"
              aria-label="メニュー"
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
            <nav className="px-6 py-4 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block py-3 px-4 rounded transition-colors ${
                    isActive(item.path)
                      ? 'bg-[#2563EB]/10 text-[#2563EB] font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <Link
                to="/contact"
                className="block w-full py-3 px-4 bg-[#2563EB] text-white text-center font-medium rounded hover:bg-[#1d4ed8] transition-colors"
              >
                お問い合わせ
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-20">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#1a2744] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12 mb-8 sm:mb-12">
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <img
                  src="https://static.readdy.ai/image/a547e5108220e49fc6552e81382cf2b5/6231042b200aa81403b8b2d064222f5d.png"
                  alt="Lill Logo"
                  className="h-8 w-auto brightness-0 invert"
                />
              </div>
              <p className="text-white/70 mb-4 sm:mb-6 leading-relaxed text-sm">
                合同会社Lill<br />
                〒204-0001<br />
                東京都清瀬市下宿2丁目439-3
              </p>
            </div>

            <div className="sm:col-span-2 lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 gap-6 sm:gap-8">
              <div>
                <h3 className="text-white font-semibold mb-3 sm:mb-4 text-sm">Navigation</h3>
                <ul className="space-y-2">
                  {navItems.map((item) => (
                    <li key={item.path}>
                      <Link to={item.path} className="text-white/70 hover:text-white transition-colors text-sm whitespace-nowrap">
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-3 sm:mb-4 text-sm">Services</h3>
                <ul className="space-y-2">
                  <li><a href="https://teal-me.io/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors text-sm whitespace-nowrap">Teal</a></li>
                  <li><Link to="/service" className="text-white/70 hover:text-white transition-colors text-sm whitespace-nowrap">AI活用設計</Link></li>
                  <li><a href="https://sakuhp.readdy.co/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors text-sm whitespace-nowrap">サクッとHP</a></li>
                  <li><Link to="/service" className="text-white/70 hover:text-white transition-colors text-sm whitespace-nowrap">システム開発</Link></li>
                </ul>
              </div>

              <div className="col-span-2 sm:col-span-1">
                <h3 className="text-white font-semibold mb-3 sm:mb-4 text-sm">Links</h3>
                <ul className="space-y-2">
                  <li>
                    <a
                      href="https://note.com/ai_kaishainda"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/70 hover:text-white transition-colors text-sm whitespace-nowrap inline-flex items-center gap-2"
                    >
                      note
                      <i className="ri-external-link-line text-xs" />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="pt-6 sm:pt-8 border-t border-white/10 text-center">
            <p className="text-white/50 text-xs sm:text-sm">© 2025 合同会社Lill All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}